import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader, CheckCircle, XCircle } from 'lucide-react'
import { setAuthToken } from '../utils/auth'

export default function MagicLinkPage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    const authenticateWithMagicLink = async () => {
      if (!code) {
        setStatus('error')
        setMessage('Invalid magic link - no code provided')
        return
      }

      try {
        const response = await fetch('/api/auth/magic-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ code })
        })

        const data = await response.json()

        if (!response.ok) {
          setStatus('error')
          setMessage(data.message || 'Failed to authenticate with magic link')
          return
        }

        // Store token
        setAuthToken(data.token)
        localStorage.setItem('user', JSON.stringify(data.user))

        setStatus('success')
        setMessage('Successfully authenticated! Redirecting to trust center...')

        // Redirect to trust center after 2 seconds
        setTimeout(() => {
          navigate('/evidence')
        }, 2000)

      } catch (error) {
        console.error('Magic link auth error:', error)
        setStatus('error')
        setMessage('An unexpected error occurred. Please try again or contact support.')
      }
    }

    authenticateWithMagicLink()
  }, [code, navigate])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 max-w-md w-full">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <Loader className="h-12 w-12 sm:h-16 sm:w-16 animate-spin text-blue-600 mx-auto mb-4" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Authenticating...
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Please wait while we verify your magic link
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Welcome!
              </h1>
              <p className="text-sm sm:text-base text-gray-600">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 sm:h-16 sm:w-16 text-red-600 mx-auto mb-4" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Authentication Failed
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mb-6">{message}</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation text-base sm:text-lg font-medium w-full sm:w-auto"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
